import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";

import { App } from "@/App";
import { AppErrorBoundary } from "@/components/ErrorBoundary";
import "@fontsource-variable/geist";
import { applyTheme, storedTheme } from "@/lib/theme";
import "@fontsource-variable/geist-mono";
import "@/index.css";
import { reloadOnStaleChunks } from "@/lib/version";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Content changes when this editor changes it, not on its own, so
      // refetching on every window focus is noise rather than freshness.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      // A 401 is handled by the client's own refresh; retrying past that just
      // delays the login screen someone is waiting for.
      retry: (failureCount, error) =>
        failureCount < 2 &&
        !(error instanceof Error && error.message.includes("authenticated")),
    },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("index.html is missing #root");

reloadOnStaleChunks();

// Before the first paint, so a chosen theme does not flash the other one.
applyTheme(storedTheme());

createRoot(root).render(
  <StrictMode>
    {/* Outside the providers: if one of them throws while mounting, this is
        the only thing left to say so. */}
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
);

// The app's code ran: public/boot.js stands down. From here a failure is the
// error boundary's to show.
(window as Window & { fmBooted?: boolean }).fmBooted = true;
